import React from 'react';
import { Switch, Route, Link, useRouteMatch } from 'react-router-dom';

import paths from '../../../constants/routes.json';
import { ListingDetails_ListingData_listing } from '../../../types/graph/ListingDetails_ListingData';
import Details from './Property/Details';
import Documents from './Property/Documents';
import Images from './Property/Images';
import Videos from './Property/Videos';
import Walkthrough from './Property/Walkthrough';

interface IProps {
  listing: ListingDetails_ListingData_listing;
}

const Property: React.FC<IProps> = ({ listing }) => {
  return (
    <div>
      <main className="relative">
        <div className="max-w-screen-xl mx-auto pb-6 lg:pb-16">
          <div className="overflow-hidden">
            <div className="lg:grid lg:grid-cols-12">
              <aside className="ml-4 py-6 lg:col-span-2">
                <nav>
                  {/* Current: "bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary", Default: "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900" */}
                  <Link
                    to={`${paths.LISTINGS}/${listing.id}/property/details`}
                    className={`${
                      useRouteMatch({
                        path: paths.LISTING_PROPERTY_DETAILS,
                        exact: true,
                      })
                        ? ` bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary `
                        : `border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900`
                    } rounded-l-lg group border-l-4 px-3 py-2 flex items-center text-sm font-medium`}
                    aria-current="page"
                  >
                    {/* Current: "text-primary group-hover:text-primary", Default: "text-gray-400 group-hover:text-gray-500" */}
                    {/* Heroicon name: outline/user-circle */}
                    <svg
                      className={`${
                        useRouteMatch({
                          path: paths.LISTING_PROPERTY_DETAILS,
                          exact: true,
                        })
                          ? `text-primary group-hover:text-primary`
                          : `text-gray-400 group-hover:text-gray-500`
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="truncate">Details</span>
                  </Link>
                  <Link
                    to={`${paths.LISTINGS}/${listing.id}/property/images`}
                    className={`${
                      useRouteMatch(paths.LISTING_PROPERTY_IMAGES)
                        ? ` bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary `
                        : `border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900`
                    } rounded-l-lg group border-l-4 px-3 py-2 flex items-center text-sm font-medium`}
                  >
                    {/* <!-- Heroicon name: outline/users --> */}
                    <svg
                      className={`${
                        useRouteMatch({
                          path: paths.LISTING_PROPERTY_IMAGES,
                        })
                          ? `text-primary group-hover:text-primary`
                          : `text-gray-400 group-hover:text-gray-500`
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="truncate">Images</span>
                  </Link>
                  <Link
                    to={`${paths.LISTINGS}/${listing.id}/property/videos`}
                    className={`${
                      useRouteMatch(paths.LISTING_PROPERTY_VIDEOS)
                        ? ` bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary `
                        : `border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900`
                    } rounded-l-lg group border-l-4 px-3 py-2 flex items-center text-sm font-medium`}
                  >
                    {/* Heroicon name: outline/key */}
                    <svg
                      className={`${
                        useRouteMatch({
                          path: paths.LISTING_PROPERTY_VIDEOS,
                        })
                          ? `text-primary group-hover:text-primary`
                          : `text-gray-400 group-hover:text-gray-500`
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                      />
                    </svg>
                    <span className="truncate">Videos</span>
                  </Link>
                  <Link
                    to={`${paths.LISTINGS}/${listing.id}/property/walkthrough`}
                    className={`${
                      useRouteMatch(paths.LISTING_PROPERTY_WALKTHROUGH)
                        ? ` bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary `
                        : `border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900`
                    } rounded-l-lg group border-l-4 px-3 py-2 flex items-center text-sm font-medium`}
                  >
                    {/* Heroicon name: outline/key */}
                    <svg
                      className={`${
                        useRouteMatch({
                          path: paths.LISTING_PROPERTY_WALKTHROUGH,
                        })
                          ? `text-primary group-hover:text-primary`
                          : `text-gray-400 group-hover:text-gray-500`
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                    <span className="truncate">Walkthrough</span>
                  </Link>
                  <Link
                    to={`${paths.LISTINGS}/${listing.id}/property/documents`}
                    className={`${
                      useRouteMatch(paths.LISTING_PROPERTY_DOCUMENTS)
                        ? ` bg-light-blue-50 border-primary text-primary hover:bg-light-blue-50 hover:text-primary `
                        : `border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900`
                    } rounded-l-lg group border-l-4 px-3 py-2 flex items-center text-sm font-medium`}
                  >
                    {/* Heroicon name: outline/key */}
                    <svg
                      className={`${
                        useRouteMatch({
                          path: paths.LISTING_PROPERTY_DOCUMENTS,
                        })
                          ? `text-primary group-hover:text-primary`
                          : `text-gray-400 group-hover:text-gray-500`
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="truncate">Documents</span>
                  </Link>
                </nav>
              </aside>
              <div className="lg:col-span-10 shadow rounded-lg bg-white">
                <div className="py-6 px-4 sm:p-6 lg:pb-8">
                  <Switch>
                    <Route path={paths.LISTING_PROPERTY_DETAILS}>
                      <Details listing={listing} />
                    </Route>
                    <Route path={paths.LISTING_PROPERTY_IMAGES}>
                      <Images listing={listing} />
                    </Route>
                    <Route path={paths.LISTING_PROPERTY_VIDEOS}>
                      <Videos listing={listing} />
                    </Route>
                    <Route path={paths.LISTING_PROPERTY_WALKTHROUGH}>
                      <Walkthrough listing={listing} />
                    </Route>
                    <Route path={paths.LISTING_PROPERTY_DOCUMENTS}>
                      <Documents listing={listing} />
                    </Route>
                  </Switch>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Property;
